import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiFetch, parseResponseBody } from '../utils/http';
import {
  buildCapabilityStatus,
  findCapabilityEntry,
  isCapabilityAccessible,
  matchesHrefBinding,
  matchesRouteIdBinding,
  matchesScreenBinding,
} from './capabilityMatrix';

const CapabilityContext = createContext(null);
const CAPABILITY_ENDPOINT = '/api/v1/capabilities';
const FRONTEND_APP = 'frontend';

export const CapabilityProvider = ({
  children,
  surfaceVariant = 'desktop',
  enabled = true,
}) => {
  const [matrix, setMatrix] = useState(null);
  const [status, setStatus] = useState(enabled ? 'loading' : 'idle');
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (!enabled) {
      setMatrix(null);
      setStatus('idle');
      setError(null);
      return undefined;
    }

    const loadCapabilityMatrix = async () => {
      setStatus('loading');
      setError(null);

      const query = new URLSearchParams({
        consumer: FRONTEND_APP,
        surface: surfaceVariant,
      });

      try {
        const response = await apiFetch(`${CAPABILITY_ENDPOINT}?${query.toString()}`, {
          method: 'GET',
          headers: {
            'X-Loopino-Client-App': FRONTEND_APP,
            'X-Loopino-Client-Surface': surfaceVariant,
          },
        });

        const payload = await parseResponseBody(response);

        if (!response.ok) {
          const requestError = new Error(payload?.message || 'Unable to load capability matrix');
          requestError.status = response.status;
          throw requestError;
        }

        if (!cancelled) {
          setMatrix(payload);
          setStatus('ready');
        }
      } catch (loadError) {
        if (!cancelled) {
          setMatrix(null);
          setStatus('error');
          setError(loadError);
        }
      }
    };

    loadCapabilityMatrix();

    return () => {
      cancelled = true;
    };
  }, [enabled, surfaceVariant]);

  const routes = matrix?.routes || [];
  const ctas = matrix?.ctas || [];

  const getRouteStateForHref = useCallback((href) => (
    buildCapabilityStatus(findCapabilityEntry(routes, (binding) => matchesHrefBinding(binding, href)))
  ), [routes]);

  const getRouteStateForScreen = useCallback((screen) => (
    buildCapabilityStatus(findCapabilityEntry(routes, (binding) => matchesScreenBinding(binding, screen)))
  ), [routes]);

  const getRouteStateForRouteId = useCallback((routeId) => (
    buildCapabilityStatus(findCapabilityEntry(routes, (binding) => matchesRouteIdBinding(binding, routeId)))
  ), [routes]);

  const getNavigationStateForHref = useCallback((href) => {
    const ctaStatus = buildCapabilityStatus(findCapabilityEntry(ctas, (binding) => matchesHrefBinding(binding, href)));
    if (ctaStatus.managed) {
      return ctaStatus;
    }
    return getRouteStateForHref(href);
  }, [ctas, getRouteStateForHref]);

  const getNavigationStateForScreen = useCallback((screen) => {
    const ctaStatus = buildCapabilityStatus(findCapabilityEntry(ctas, (binding) => matchesScreenBinding(binding, screen)));
    if (ctaStatus.managed) {
      return ctaStatus;
    }
    return getRouteStateForScreen(screen);
  }, [ctas, getRouteStateForScreen]);

  const value = useMemo(() => ({
    matrix,
    status,
    error,
    isReady: status === 'ready',
    isLoading: status === 'loading',
    isError: status === 'error',
    getRouteStateForHref,
    getRouteStateForScreen,
    getRouteStateForRouteId,
    getNavigationStateForHref,
    getNavigationStateForScreen,
    isAccessible: isCapabilityAccessible,
  }), [
    error,
    getNavigationStateForHref,
    getNavigationStateForScreen,
    getRouteStateForHref,
    getRouteStateForRouteId,
    getRouteStateForScreen,
    matrix,
    status,
  ]);

  return (
    <CapabilityContext.Provider value={value}>
      {children}
    </CapabilityContext.Provider>
  );
};

export const useCapabilities = () => {
  const context = useContext(CapabilityContext);
  if (!context) {
    throw new Error('useCapabilities must be used within a CapabilityProvider');
  }
  return context;
};

export default CapabilityContext;
